/**
 * AppShell — the chrome wrapping every authenticated page.
 *
 * Top nav with logo, language toggle, wallet pill, loyalty pill, and
 * profile menu. Children render in a `<main>` landmark for screen
 * readers. (wouter doesn't use Outlet — the router passes children
 * directly.)
 *
 * Preserves the WCAG 2.4.1 skip-link, the sticky-header chrome, and
 * the bilingual h1 from the original monolith.
 */

import * as React from 'react';
import { Link } from 'wouter';

import { useLang, useT } from '../providers/LangProvider';
import { Settings, Sun, Moon } from 'lucide-react';
import { useTheme } from '../hooks/use-theme';

import { LoyaltyPill } from './LoyaltyPill';
import { ProfileMenu } from './ProfileMenu';
import { WalletPill } from './WalletPill';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const t = useT();
  const [lang, setLang] = useLang();
  const { isDark, toggle: toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-[#0f0a1e] font-sans">
      {/* Skip to main — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:start-2 rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
      >
        {t.skipToMain}
      </a>

      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
        <div className="flex items-center gap-2">
          <Link
            href="/"
            className="text-xl font-bold text-purple-900 dark:text-purple-300 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded"
            style={{ fontFamily: 'system-ui, sans-serif' }}
            aria-label={t.title}
          >
            {t.title}
          </Link>
          <span
            className="hidden rounded-full bg-purple-100 dark:bg-purple-900/50 px-2 py-0.5 text-xs text-purple-700 dark:text-purple-300 sm:inline"
            aria-hidden="true"
          >
            {t.beta}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 sm:flex">
            <WalletPill />
            <LoyaltyPill />
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
            className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            aria-label={t.langSwitchAria}
          >
            {t.lang}
          </button>
          <Link href="/settings">
            <button
              type="button"
              aria-label={t.settingsAria}
              className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            >
              <Settings className="h-4 w-4" />
            </button>
          </Link>
          <ProfileMenu />
        </div>
      </header>

      <main id="main-content" className="mx-auto max-w-3xl px-4 py-6">
        {/* Mobile-only pill row — pills move below the header on narrow viewports */}
        <div className="mb-4 flex gap-2 sm:hidden">
          <WalletPill />
          <LoyaltyPill />
        </div>
        {children}
      </main>

      {/* WhatsApp support shortcut — always-visible FAB */}
      <a
        href={`https://wa.me/${(import.meta.env.VITE_SUPPORT_WHATSAPP_NUMBER as string) ?? '966500000000'}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 end-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg hover:bg-green-600 hover:shadow-xl transition-all hover:scale-105 focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2"
      >
        <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.122.554 4.112 1.523 5.838L.057 23.786A.5.5 0 0 0 .545 24l6.103-1.597A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.906 0-3.687-.508-5.22-1.394l-.373-.222-3.862 1.01 1.032-3.763-.243-.39A9.953 9.953 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
        </svg>
      </a>
    </div>
  );
}
